import { Box, Button, Card, Group, Stack, Text } from "@mantine/core";
import { useCallback, useMemo, useState } from "react";

import { formatShare } from "../share/format";
import type { Attempt } from "../storage";

export interface ShareCardProps {
  readonly puzzleId: string;
  readonly attempts: readonly Attempt[];
}

const COPIED_FLASH_MS = 1800;

export function ShareCard({ puzzleId, attempts }: ShareCardProps) {
  const text = useMemo(() => formatShare(puzzleId, attempts), [puzzleId, attempts]);
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    // Clipboard writes can fail in tabs without focus or under some
    // permission configurations; on failure leave the preview text in
    // place so the user can still select-and-copy by hand.
    navigator.clipboard.writeText(text).then(
      () => {
        setCopied(true);
        setTimeout(() => {
          setCopied(false);
        }, COPIED_FLASH_MS);
      },
      () => undefined,
    );
  }, [text]);

  return (
    <Card withBorder padding="md" aria-label="share">
      <Stack gap="xs">
        <Text fw={600} size="sm">
          share your run
        </Text>
        <Box
          component="pre"
          aria-label="share preview"
          style={{
            margin: 0,
            fontFamily:
              'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, Consolas, monospace',
            fontSize: 14,
            whiteSpace: "pre-wrap",
          }}
        >
          {text}
        </Box>
        <Group>
          <Button onClick={handleCopy} variant="default" size="xs">
            {copied ? "copied!" : "copy to clipboard"}
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}
